import React, { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, useNavigate, Outlet } from 'react-router-dom';
import './App.css';

// サービスのインポート
import { configService } from './services/configService';
import { initializeAllowedIPs } from './data/storeData';

// ルート定数をインポート
import { ROUTES } from './config/routes';

// コンポーネントのインポート
import { ToastProvider } from './components/ui/ToastManager';
import { AuthProvider } from './context/AuthContext';
import { RegistrationFlowProvider } from './context/RegistrationFlowContext';
import { RegistrationFlowRoute, RegistrationFlowProtectedGuard } from './context/RegistrationRouteGuard';
import ProtectedRoute from './components/ProtectedRoute';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import PublicOnlyRoute from './components/PublicOnlyRoute';
import Header from './components/header.js';
import TermsAgreementForm from './pages/TermsAgreementForm';
import AuthCodeForm from './pages/AuthCodeForm';
import RegisterForm from './pages/RegisterForm';
import CreditCardForm from './pages/CreditCardForm';
import RegistrationCompletePage from './pages/RegistrationCompletePage';
import HomePage from './pages/HomePage';
import CreditCardEditPage from './pages/CreditCardEditForm';
import MemberEditPage from './pages/MemberEditPage';
import EmailChangeForm from './pages/EmailChangeForm';
import PasswordChangeForm from './pages/PasswordChangeForm';
import NotFoundPage from './pages/NotFoundPage';
import LoginForm from './pages/LoginForm';
import PasswordResetForm from './pages/PasswordResetForm';
import NewPasswordForm from './pages/NewPasswordForm';

// Wrapper関数たち
const TermsWrapper = () => {
  const navigate = useNavigate();
  const handleTermsComplete = (data) => {
    navigate(ROUTES.AUTH, { replace: true });
  };
  return (
    <TermsAgreementForm 
      onTermsComplete={handleTermsComplete}
      language="ja"
    />
  );
};

const AuthWrapper = () => {
  const navigate = useNavigate();
  const handleAuthComplete = (email) => {
    // 認証完了後の処理
    navigate(ROUTES.REGISTER, { replace: true });
  };
  return (
    <AuthCodeForm
      onAuthComplete={handleAuthComplete}
      language="ja"
    />
  );
};

const RegisterWrapper = () => {
  return <RegisterForm language="ja" />;
};

const CreditCardWrapper = () => {
  const navigate = useNavigate();
  const handleCreditCardComplete = (data) => {
    // クレジットカード登録完了後の処理
    navigate(`${ROUTES.REGISTRATION_COMPLETE}?memberId=${data.memberId}`, {
      state: { 
        message: 'クレジットカード登録が完了しました！',
        memberData: data.memberData 
      }
    });
  };
  return <CreditCardForm onCreditCardComplete={handleCreditCardComplete} />;
};

const LoginWrapper = () => {
  return <LoginForm language="ja" />;
};

const PasswordResetWrapper = () => {
  return <PasswordResetForm language="ja" />;
};

const CreditCardEditWrapper = () => {
  return <CreditCardEditPage language="ja" />;
};

const MemberEditWrapper = () => {
  return <MemberEditPage language="ja" />;
};

const EmailChangeWrapper = () => {
  return <EmailChangeForm language="ja" />;
};

const PasswordChangeWrapper = () => {
  return <PasswordChangeForm language="ja" />;
};

// レイアウトコンポーネント
const RootLayout = () => {
  const [configLoaded, setConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState(null);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // ① バックエンドから設定を読み込む
        await configService.fetchConfig();
        
        // ② 許可IPリストを初期化
        await initializeAllowedIPs();
        
        setConfigLoaded(true);
      } catch (error) {
        console.error('[APP] Failed to initialize application:', error);
        setConfigError(error);
        // エラーでもアプリは起動させる(設定なしでも動作するように)
        setConfigLoaded(true);
      }
    };

    initializeApp();
  }, []);

  // 初期化中はローディング表示
  if (!configLoaded) {
    return (
      <div className="App">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontSize: '18px',
          color: '#666'
        }}>
          アプリケーションを初期化しています...
        </div>
      </div>
    );
  }

  // エラーがあれば警告表示(開発用)
  if (configError && process.env.NODE_ENV === 'development') {
    console.warn('[APP] Running with config error:', configError.message);
  }

  return (
    <AuthProvider>
      <RegistrationFlowProvider>
        <ToastProvider>
          <div className="App">
            <Header />
            <Outlet />
          </div>
        </ToastProvider>
      </RegistrationFlowProvider>
    </AuthProvider>
  );
};

// ルーター定義（createBrowserRouter使用）
const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    // 配下のどの画面で例外が発生してもユーザー向けのエラー画面を表示する
    // （react-router既定のスタックトレース画面を出さないため）
    errorElement: <RouteErrorBoundary />,
    children: [
      // ルートパス - ログイン画面
      {
        index: true,
        element: <Navigate to={ROUTES.LOGIN} replace />
      },
      
      // ログイン画面（認証済みユーザーはアクセス不可）
      {
        path: ROUTES.LOGIN,
        element: (
          <PublicOnlyRoute>
            <LoginWrapper />
          </PublicOnlyRoute>
        )
      },
      
      // パスワードリセット画面（認証済みユーザーはアクセス不可）
      {
        path: ROUTES.FORGOT_PASSWORD,
        element: (
          <PublicOnlyRoute>
            <PasswordResetWrapper />
          </PublicOnlyRoute>
        )
      },
      
      // 新しいパスワード設定画面（Firebase Authリンクから）
      {
        path: ROUTES.RESET_PASSWORD,
        element: <NewPasswordForm />
      },
      
      // 利用規約同意画面
      {
        path: ROUTES.TERMS,
        element: (
          <PublicOnlyRoute>
            <TermsWrapper />
          </PublicOnlyRoute>
        )
      },
      
      // 認証画面（メール認証）
      {
        path: ROUTES.AUTH,
        element: (
          <PublicOnlyRoute>
            <RegistrationFlowRoute>
              <AuthWrapper />
            </RegistrationFlowRoute>
          </PublicOnlyRoute>
        )
      },
      
      // 会員登録画面（Firebaseアカウント作成含む）
      {
        path: ROUTES.REGISTER,
        element: (
          <PublicOnlyRoute>
            <RegistrationFlowRoute>
              <RegisterWrapper />
            </RegistrationFlowRoute>
          </PublicOnlyRoute>
        )
      },
      
      // クレジットカード情報入力画面（認証必須だが登録フロー継続）
      {
        path: ROUTES.CREDIT_CARD_INPUT,
        element: (
          <RegistrationFlowProtectedGuard>
            <CreditCardWrapper />
          </RegistrationFlowProtectedGuard>
        )
      },
      
      // 会員登録完了画面（認証必須だが登録フロー継続）
      {
        path: ROUTES.REGISTRATION_COMPLETE,
        element: (
          <RegistrationFlowProtectedGuard>
            <RegistrationCompletePage />
          </RegistrationFlowProtectedGuard>
        )
      },
      
      // ホーム画面
      {
        path: ROUTES.HOME,
        element: (
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        )
      },
      
      // クレジットカード変更画面
      {
        path: ROUTES.CREDIT_CARD_EDIT,
        element: (
          <ProtectedRoute>
            <CreditCardEditWrapper />
          </ProtectedRoute>
        )
      },
      
      // 会員情報編集画面
      {
        path: ROUTES.MEMBER_EDIT,
        element: (
          <ProtectedRoute>
            <MemberEditWrapper />
          </ProtectedRoute>
        )
      },

      // メールアドレス変更画面
      {
        path: ROUTES.EMAIL_CHANGE,
        element: (
          <ProtectedRoute>
            <EmailChangeWrapper />
          </ProtectedRoute>
        )
      },

      // パスワード変更画面
      {
        path: ROUTES.PASSWORD_CHANGE,
        element: (
          <ProtectedRoute>
            <PasswordChangeWrapper />
          </ProtectedRoute>
        )
      },

      // 404 エラーページ
      {
        path: ROUTES.NOT_FOUND,
        element: <NotFoundPage />
      }
    ]
  }
]);

function App() {
  return <RouterProvider router={router} />;
}

export default App;